import { useEffect, useState } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext.jsx';
import { Logo } from '../ui/Logo.jsx';

function Hamburger({ onClick, open }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-ink hover:bg-cream"
      aria-label={open ? 'Închide meniul' : 'Deschide meniul'}
      aria-expanded={open}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {open ? (
          <>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </>
        ) : (
          <>
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </>
        )}
      </svg>
    </button>
  );
}

const NAV_ITEMS = [
  { to: '/', label: 'Dashboard', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
  ) },
  { to: '/cars', label: 'Mașini', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>
  ) },
  { to: '/alerts', label: 'Alerte', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
  ) },
  { to: '/users', label: 'Utilizatori', adminOnly: true, icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
  ) },
  { to: '/account', label: 'Contul meu', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  ) },
];

export function AppShell() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Închide sidebar-ul la navigare
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Esc închide
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <header className="sticky top-0 z-20 border-b border-border bg-paper/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <Hamburger onClick={() => setOpen(true)} open={open} />
          <Link to="/" aria-label="daruto" className="flex-shrink-0">
            <Logo size={30} />
          </Link>
          <div className="flex-1" />
          <Link to="/cars/new" className="btn bg-coral text-white text-sm hover:brightness-95">
            + Mașină
          </Link>
        </div>
      </header>

      {/* Overlay */}
      <div
        onClick={() => setOpen(false)}
        className={`fixed inset-0 z-40 bg-ink/50 backdrop-blur-sm transition-opacity ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!open}
      />

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 z-50 flex h-full w-72 max-w-[85vw] flex-col bg-white shadow-xl transition-transform ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-hidden={!open}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-4">
          <Logo size={30} />
          <Hamburger onClick={() => setOpen(false)} open={true} />
        </div>

        {user && (
          <div className="border-b border-border px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">
              {user.role === 'admin' ? 'Administrator' : 'Membru'}
            </div>
            <div className="mt-0.5 truncate font-semibold text-ink">{user.name}</div>
            <div className="truncate text-xs text-muted">{user.tenant?.name}</div>
          </div>
        )}

        <nav className="flex-1 space-y-0.5 px-2 py-3">
          {NAV_ITEMS.filter((i) => !i.adminOnly || user?.role === 'admin').map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-coral/10 text-coral'
                    : 'text-ink hover:bg-cream'
                }`
              }
            >
              <span className="text-muted group-hover:text-ink">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border px-4 py-3">
          <Link
            to="/cars/new"
            className="btn-primary w-full"
          >
            + Adaugă mașină
          </Link>
          <button
            onClick={logout}
            className="mt-2 w-full rounded-lg px-3 py-2 text-sm text-muted hover:bg-cream"
          >
            Deconectare
          </button>
        </div>
      </aside>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
        <Outlet />
      </main>

      <footer className="mt-8 border-t border-border bg-paper">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <Logo size={28} />
              <p className="mt-3 text-xs text-muted">
                Platformă pentru gestionarea flotei auto: documente, alerte expirare, închirieri și contracte.
              </p>
            </div>

            {/* Navigare */}
            <div>
              <div className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-muted">Navigare</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-ink hover:text-coral">Dashboard</Link></li>
                <li><Link to="/cars" className="text-ink hover:text-coral">Mașini</Link></li>
                <li><Link to="/alerts" className="text-ink hover:text-coral">Alerte</Link></li>
                {user?.role === 'admin' && (
                  <li><Link to="/users" className="text-ink hover:text-coral">Utilizatori</Link></li>
                )}
                <li><Link to="/account" className="text-ink hover:text-coral">Contul meu</Link></li>
              </ul>
            </div>

            {/* Acțiuni rapide */}
            <div>
              <div className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-muted">Acțiuni</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/cars/new" className="text-ink hover:text-coral">+ Adaugă mașină</Link></li>
                <li><Link to="/cars" className="text-ink hover:text-coral">Vezi flota</Link></li>
                {user?.role === 'admin' && (
                  <li><Link to="/users" className="text-ink hover:text-coral">Invită utilizator</Link></li>
                )}
              </ul>
            </div>

            {/* Info */}
            <div>
              <div className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-muted">Info</div>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="mailto:app@daruto.ro" className="text-ink hover:text-coral">Suport: app@daruto.ro</a>
                </li>
                <li>
                  <a href="https://dpixel.agency" target="_blank" rel="noreferrer" className="text-ink hover:text-coral">
                    DPIXEL AGENCY
                  </a>
                </li>
                <li className="font-mono text-xs text-muted">v1.2</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 flex flex-col items-center justify-between gap-2 border-t border-border pt-4 text-[11px] text-muted sm:flex-row">
            <span>© {new Date().getFullYear()} daruto. Toate drepturile rezervate.</span>
            <span>
              Developed by{' '}
              <a href="https://dpixel.agency" target="_blank" rel="noreferrer" className="font-semibold text-ink hover:text-coral">
                DPIXEL AGENCY
              </a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
